import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import useEntregablesEstudiante from "../hooks/useEntregablesEstudiante";
import { usePermissions } from "../../../../utils/permissions";
// .entregables-panel* (matriculas.css) y .educacion-quick-menu / .menu-item
// (estudiantes.css) ya existen — se reutilizan tal cual, sin CSS nuevo.
import "../../matriculas/styles/matriculas.css";
import "../styles/estudiantes.css";
import "../../../../styles/managementTheme.css";

const OBSERVACION_MAX = 80;

// Mismo vocabulario de colores que ya se usa para estado_pago en
// MatriculasTable (verde/amarillo/rojo/gris) — estado_matricula no tenia
// una asignacion de color propia todavia en el resto del sistema.
const ESTADO_MATRICULA = {
  activa: { label: "Activa", badge: "badge bg-success-subtle text-success" },
  reservada: { label: "Reservada", badge: "badge bg-warning-subtle text-warning" },
  suspendida: { label: "Suspendida", badge: "badge bg-danger-subtle text-danger" },
  finalizada: { label: "Finalizada", badge: "badge bg-secondary-subtle text-muted" },
  anulada: { label: "Anulada", badge: "badge bg-danger-subtle text-danger" },
};

const ESTADO_BADGE = {
  entregado: "badge bg-success",
  devuelto: "badge bg-warning text-dark",
  anulado: "badge bg-secondary",
};

const formatFecha = (value) => {
  if (!value) return "-";
  const datePart = String(value).split("T")[0].split(" ")[0];
  const match = datePart.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (match) {
    const [, year, month, day] = match;
    return `${day}/${month}/${year}`;
  }
  return datePart;
};

const itemKey = (item) => `${item.matricula_id}-${item.entregable_id}`;

const nombreConTalla = (item) => (item.talla ? `${item.nombre} (${item.talla})` : item.nombre);

// Icono por nombre de entregable. Bootstrap Icons no trae un icono de
// camiseta/buzo especifico — bi-bag es el mas cercano disponible; el resto
// tiene una coincidencia razonable.
const ICONO_POR_NOMBRE = {
  "camisa": "bi-bag",
  "buzo": "bi-bag",
  "mochila": "bi-backpack",
  "audífonos": "bi-headphones",
  "audifonos": "bi-headphones",
  "guantes tácticos": "bi-hand-index-thumb",
  "guantes tacticos": "bi-hand-index-thumb",
  "gafas tácticas": "bi-sunglasses",
  "gafas tacticas": "bi-sunglasses",
  "guía de estudio": "bi-journal-bookmark",
  "guia de estudio": "bi-journal-bookmark",
  "diploma": "bi-award",
  "mención de honor": "bi-patch-check",
  "mencion de honor": "bi-patch-check",
  "credencial institucional": "bi-person-vcard",
};

const iconoEntregable = (nombre = "", tipo = "articulo") => {
  const key = nombre.trim().toLowerCase();
  return ICONO_POR_NOMBRE[key] || (tipo === "documento" ? "bi-file-earmark-text" : "bi-box-seam");
};

// ── Modal de solo lectura: ver la nota completa (usuarios sin permiso de
// edicion) — no replica el resto del modal de notas de Instagram porque
// "compartida con amigos" / "dejar nota nueva" son propios de esa red y no
// aplican aqui; solo se necesita poder leer el texto completo.

function VerNotaModal({ item, onClose }) {
  if (!item) return null;

  return (
    <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: "rgba(15,23,42,0.45)" }} role="dialog">
      <div className="modal-dialog modal-dialog-centered" role="document">
        <div className="modal-content">
          <div className="modal-header border-0">
            <h5 className="modal-title">{item.nombre}</h5>
            <button type="button" className="btn-close" onClick={onClose} />
          </div>
          <div className="modal-body">
            <p className="mb-0">{item.observacion}</p>
          </div>
          <div className="modal-footer border-0">
            <button type="button" className="btn btn-outline-secondary" onClick={onClose}>Cerrar</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Modal liviano: editar talla / observacion de un entregable articulo ──────

function EditarDetalleModal({ item, onClose, onGuardar, saving }) {
  const [talla, setTalla] = useState(item?.talla || "");
  const [observacion, setObservacion] = useState(item?.observacion || "");

  useEffect(() => {
    setTalla(item?.talla || "");
    setObservacion(item?.observacion || "");
  }, [item]);

  if (!item) return null;

  return (
    <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: "rgba(15,23,42,0.45)" }} role="dialog">
      <div className="modal-dialog modal-dialog-centered" role="document">
        <div className="modal-content">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              onGuardar({ talla: talla || null, observaciones: observacion || null });
            }}
          >
            <div className="modal-header border-0">
              <h5 className="modal-title">
                <i className="bi bi-pencil-square me-2 text-primary" />
                Editar {item.nombre}
              </h5>
              <button type="button" className="btn-close" onClick={onClose} />
            </div>
            <div className="modal-body">
              {item.requiere_talla && (
                <div className="mb-3">
                  <label className="form-label">Talla</label>
                  <input className="form-control" value={talla} onChange={(e) => setTalla(e.target.value)} autoFocus />
                </div>
              )}
              <div className="mb-1">
                <label className="form-label">Observacion</label>
                <textarea
                  className="form-control"
                  rows={2}
                  maxLength={OBSERVACION_MAX}
                  value={observacion}
                  onChange={(e) => setObservacion(e.target.value.slice(0, OBSERVACION_MAX))}
                />
                <small className="text-muted d-block text-end mt-1">
                  {observacion.length}/{OBSERVACION_MAX}
                </small>
              </div>
            </div>
            <div className="modal-footer border-0">
              <button type="button" className="btn btn-outline-secondary" onClick={onClose}>Cancelar</button>
              <button type="submit" className="btn management-btn-primary" disabled={saving}>
                {saving ? "Guardando..." : "Guardar"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default function EntregablesEstudianteTab({ estudianteId, matriculaIdInicial }) {
  const {
    grupos, loading, error, savingId, recargar,
    guardarEntregable, generarDocumento, descargarDocumento, tieneGenerador,
  } = useEntregablesEstudiante(estudianteId);
  const { hasPermission } = usePermissions();
  const puedeGestionar = hasPermission("matriculas.actualizar");
  // Generar/descargar Diploma y Mencion usa el mismo permiso que ya exige
  // el backend en /api/diplomas y /api/menciones-honor.
  const puedeGestionarDocumentos = hasPermission("promociones.listar");

  const [openMatriculaId, setOpenMatriculaId] = useState(null);
  const [menuOpenKey, setMenuOpenKey] = useState(null);
  const [menuPosition, setMenuPosition] = useState(null);
  const [editando, setEditando] = useState(null);
  const [viendoNota, setViendoNota] = useState(null);
  const menuRef = useRef(null);

  // Al cargar, abre la matricula pedida por query param (?matriculaId=) si
  // vino de un enlace especifico (ej. desde 'Editar matricula'); si no,
  // la mas reciente por defecto (grupos ya vienen ordenados DESC por
  // fecha_matricula desde el backend).
  useEffect(() => {
    if (!grupos.length || openMatriculaId !== null) return;
    const idPedido = Number(matriculaIdInicial);
    const existe = grupos.some((g) => g.matricula_id === idPedido);
    setOpenMatriculaId(existe ? idPedido : grupos[0].matricula_id);
  }, [grupos, openMatriculaId, matriculaIdInicial]);

  const toggleGrupo = (matriculaId) => {
    setOpenMatriculaId((prev) => (prev === matriculaId ? null : matriculaId));
  };

  const cerrarMenu = useCallback(() => {
    setMenuOpenKey(null);
    setMenuPosition(null);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const inMenu = menuRef.current?.contains(event.target);
      const fromTrigger = event.target instanceof Element && event.target.closest(".entregable-menu-trigger");
      if (!inMenu && !fromTrigger) cerrarMenu();
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [cerrarMenu]);

  useEffect(() => {
    if (!menuOpenKey) return undefined;
    window.addEventListener("scroll", cerrarMenu, true);
    window.addEventListener("resize", cerrarMenu);
    return () => {
      window.removeEventListener("scroll", cerrarMenu, true);
      window.removeEventListener("resize", cerrarMenu);
    };
  }, [menuOpenKey, cerrarMenu]);

  const toggleMenu = (event, item) => {
    event.preventDefault();
    event.stopPropagation();
    const key = itemKey(item);
    if (menuOpenKey === key) {
      cerrarMenu();
      return;
    }
    const rect = event.currentTarget.getBoundingClientRect();
    const menuWidth = 200;
    const menuHeight = 180;
    const gap = 6;
    const viewportPadding = 8;
    let left = rect.right - menuWidth;
    left = Math.max(viewportPadding, Math.min(left, window.innerWidth - menuWidth - viewportPadding));
    let top = rect.bottom + gap;
    if (top + menuHeight > window.innerHeight - viewportPadding) {
      top = rect.top - menuHeight - gap;
    }
    setMenuOpenKey(key);
    setMenuPosition({ top: Math.max(viewportPadding, top), left, item });
  };

  const handleCambiarEstado = async (item, estado) => {
    cerrarMenu();
    await guardarEntregable(item, { estado });
  };

  const handleGenerar = async (item) => {
    cerrarMenu();
    await generarDocumento(item);
  };

  const handleDescargar = (item) => {
    cerrarMenu();
    descargarDocumento(item);
  };

  // La nota trunca el texto; al hacer clic se ve completo. Si el usuario
  // puede editar, mejor llevarlo directo al modal de edicion (ya muestra el
  // texto completo, editable); si no, un modal simple de solo lectura.
  const handleVerNota = (item) => {
    if (puedeGestionar) setEditando(item);
    else setViendoNota(item);
  };

  if (loading && !grupos.length) {
    return (
      <div className="p-4 text-center text-muted">
        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
        Cargando entregables...
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-warning d-flex align-items-center justify-content-between management-alert mb-0">
        <div>Entregables: {error}</div>
        <button type="button" className="btn btn-sm btn-outline-secondary" onClick={recargar}>Reintentar</button>
      </div>
    );
  }

  if (!grupos.length) {
    return (
      <div className="text-muted small p-3">
        Este estudiante todavia no tiene matriculas con entregables asignados.
      </div>
    );
  }

  const quickMenu =
    menuOpenKey && menuPosition && typeof document !== "undefined"
      ? createPortal(
          <div
            ref={menuRef}
            className="educacion-quick-menu"
            style={{ top: `${menuPosition.top}px`, left: `${menuPosition.left}px` }}
            role="menu"
            aria-label="Acciones del entregable"
          >
            {(() => {
              const item = menuPosition.item;
              const esGenerable = tieneGenerador(item.nombre);
              const opciones = [];

              if (esGenerable && puedeGestionarDocumentos) {
                if (item.puede_descargar) {
                  opciones.push(
                    <button key="descargar" type="button" className="menu-item" onClick={() => handleDescargar(item)}>
                      <i className="bi bi-download" />
                      Descargar
                    </button>
                  );
                }
                opciones.push(
                  <button key="generar" type="button" className="menu-item" onClick={() => handleGenerar(item)}>
                    <i className="bi bi-file-earmark-plus" />
                    {item.puede_descargar ? "Regenerar documento" : "Generar documento"}
                  </button>
                );
              }

              if (!esGenerable && puedeGestionar) {
                if (item.estado !== "entregado") {
                  opciones.push(
                    <button key="entregado" type="button" className="menu-item" onClick={() => handleCambiarEstado(item, "entregado")}>
                      <i className="bi bi-check-circle" />
                      Marcar entregado
                    </button>
                  );
                }
                // "Devuelto" solo tiene sentido si ya estaba entregado — no
                // se puede devolver algo que nunca se entrego.
                if (item.estado === "entregado") {
                  opciones.push(
                    <button key="devuelto" type="button" className="menu-item" onClick={() => handleCambiarEstado(item, "devuelto")}>
                      <i className="bi bi-box-arrow-in-left" />
                      Marcar devuelto
                    </button>
                  );
                }
                if (item.estado !== "pendiente") {
                  opciones.push(
                    <button key="pendiente" type="button" className="menu-item" onClick={() => handleCambiarEstado(item, "pendiente")}>
                      <i className="bi bi-arrow-counterclockwise" />
                      Marcar pendiente
                    </button>
                  );
                }
                opciones.push(
                  <button
                    key="editar"
                    type="button"
                    className="menu-item"
                    onClick={() => { cerrarMenu(); setEditando(item); }}
                  >
                    <i className="bi bi-pencil" />
                    Editar
                  </button>
                );
              }

              return opciones.length ? opciones : (
                <div className="text-muted small px-2 py-1">Sin acciones disponibles</div>
              );
            })()}
          </div>,
          document.body
        )
      : null;

  return (
    <div className="d-flex flex-column gap-3">
      {grupos.map((grupo) => {
        const abierto = openMatriculaId === grupo.matricula_id;
        return (
          <div key={grupo.matricula_id} className="management-card h-100 entregables-panel">
            <div
              className="d-flex align-items-center justify-content-between mb-0 entregables-panel__header"
              role="button"
              tabIndex={0}
              onClick={() => toggleGrupo(grupo.matricula_id)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  toggleGrupo(grupo.matricula_id);
                }
              }}
              style={{ cursor: "pointer" }}
            >
              <div className="flex-grow-1 me-2">
                <h6 className="mb-1 d-flex align-items-center gap-2 flex-wrap">
                  {grupo.curso_nombre || "Curso sin nombre"}
                  <span className={ESTADO_MATRICULA[grupo.estado_matricula]?.badge || "badge bg-secondary-subtle text-muted"}>
                    {ESTADO_MATRICULA[grupo.estado_matricula]?.label || grupo.estado_matricula || "-"}
                  </span>
                </h6>
                <small className="text-muted">
                  Matricula {grupo.codigo_matricula || grupo.matricula_id}
                  {grupo.numero_promocion ? ` · Promocion ${grupo.numero_promocion}` : ""}
                  {" · "}
                  {formatFecha(grupo.fecha_matricula)}
                </small>
              </div>
              <i className={`bi ${abierto ? "bi-chevron-up" : "bi-chevron-down"} entregables-panel__chevron`} />
            </div>

            {abierto && (
              <div className="mt-3">
                {grupo.entregables.length === 0 ? (
                  <div className="list-group-item text-muted small entregables-panel__empty">
                    Sin entregables asignados.
                  </div>
                ) : (
                  <div className="entregables-cards-grid">
                    {grupo.entregables.map((item) => {
                      const key = itemKey(item);
                      const generable = tieneGenerador(item.nombre);
                      const guardandoEste = savingId === item.id;
                      return (
                        <div key={key} className="entregable-card">
                          {(puedeGestionar || (generable && puedeGestionarDocumentos)) && (
                            <span
                              role="button"
                              tabIndex={0}
                              className="experiencia-menu-trigger entregable-menu-trigger entregable-card__menu"
                              title="Acciones"
                              aria-label="Acciones"
                              onClick={(e) => toggleMenu(e, item)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                  e.preventDefault();
                                  toggleMenu(e, item);
                                }
                              }}
                            >
                              <i className="bi bi-three-dots-vertical" />
                            </span>
                          )}

                          <div className="entregable-card__nota-slot">
                            {item.observacion && (
                              <div
                                className="entregable-card__nota"
                                role="button"
                                tabIndex={0}
                                title={item.observacion}
                                onClick={() => handleVerNota(item)}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter" || e.key === " ") {
                                    e.preventDefault();
                                    handleVerNota(item);
                                  }
                                }}
                              >
                                {item.observacion}
                              </div>
                            )}
                          </div>

                          <div className="entregable-card__icon">
                            <i className={`bi ${iconoEntregable(item.nombre, item.tipo)}`} />
                          </div>

                          <div className="entregable-card__nombre">{nombreConTalla(item)}</div>

                          <span className={`${ESTADO_BADGE[item.estado] || "badge bg-secondary"} entregables-panel__status`}>
                            {(item.estado || "pendiente").toUpperCase()}
                          </span>

                          {item.estado === "entregado" && item.fecha_entrega && (
                            <small className="text-muted">{formatFecha(item.fecha_entrega)}</small>
                          )}

                          {guardandoEste && (
                            <span className="spinner-border spinner-border-sm text-muted" role="status" aria-hidden="true" />
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}

      {quickMenu}

      <EditarDetalleModal
        item={editando}
        saving={savingId === editando?.id}
        onClose={() => setEditando(null)}
        onGuardar={async (cambios) => {
          const item = editando;
          setEditando(null);
          await guardarEntregable(item, cambios);
        }}
      />

      <VerNotaModal item={viendoNota} onClose={() => setViendoNota(null)} />
    </div>
  );
}
